import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import { localizeArt } from './../localize.js';
import { formatYear } from './../utils.js';
import { Loader, Paper, Text } from '@mantine/core';

// Lists a user's reviews sorted by updatedTime DESC. Each card shows
// the art thumbnail + title, the review body (rendered markdown,
// clamped to a preview height), and a "read more" link to the
// dedicated review page.
// `initialItems` / `onLoaded` let the parent cache the fetched list
// across tab remounts so switching Library → Reviews → Library →
// Reviews doesn't refetch every time.
const UserReviewsList = ({ userId, initialItems = null, onLoaded }) => {
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const [items, setItems] = useState(initialItems);

  useEffect(() => {
    if (!userId) return undefined;
    if (items !== null) return undefined; // already loaded, don't refetch
    let cancelled = false;
    axiosInstance
      .get(`/users/${userId}/reviews`)
      .then((res) => {
        if (cancelled) return;
        const next = Array.isArray(res.data) ? res.data : [];
        setItems(next);
        if (onLoaded) onLoaded(next);
      })
      .catch(() => { if (!cancelled) { setItems([]); if (onLoaded) onLoaded([]); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (items === null) {
    return (
      <div className="flex justify-center mt-8">
        <Loader size="sm" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex justify-center mt-8">
        <Text c="dimmed">
          {language === 'ru' ? 'Пока нет отзывов' : 'No reviews yet'}
        </Text>
      </div>
    );
  }

  const dt = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(language, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  return (
    <div className="flex flex-col gap-3 mt-2">
      {items.map(({ review, art }) => {
        if (!art) return null;
        const localized = localizeArt(art, language);
        const displayName = (localized?.name || art.name || art.ruName || '') + formatYear(art.year, language);
        return (
          <Paper key={review.id} shadow="xs" radius="md" p="md" style={{ backgroundColor: 'var(--app-surface)' }}>
            <div className="flex items-start gap-3 mb-2">
              <Link to={`/arts/${art.id}`}>
                <img
                  src={art.thumbnailUrl}
                  alt=""
                  className="rounded-md w-[56px] h-[76px] object-cover"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/arts/${art.id}`}>
                  <Text size="md" fw={700}>{displayName}</Text>
                </Link>
                <Text size="xs" c="dimmed">
                  {(language === 'ru' ? 'Обновлено ' : 'Updated ') + dt(review.updatedTime)}
                </Text>
              </div>
            </div>
            <div
              style={{
                maxHeight: 220,
                overflow: 'hidden',
                position: 'relative',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.text || '') }}
            />
            <div className="mt-2">
              <Link
                to={`/arts/${art.id}/review`}
                style={{ color: 'var(--mantine-color-blue-text)', fontSize: '0.85rem' }}
              >
                {language === 'ru' ? 'Читать полностью →' : 'Read full →'}
              </Link>
            </div>
          </Paper>
        );
      })}
    </div>
  );
};

export default UserReviewsList;
